/**
 * lib/cities/city_catalog.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * RHEA City Catalog — fuente de verdad para todos los destinos soportados.
 *
 * Este archivo alimenta:
 *   • DestinationInspirePanel  — grids de inspiración filtrados por mood
 *   • lib/cities/index.ts      — resolución de texto → IATA (queryParser)
 *   • CITY_HERO_META           — gradientes y colores para city heroes
 *
 * Cada ciudad incluye:
 *   iata          — código identificador (puede ser IATA real o código RHEA para
 *                   ciudades sin aeropuerto; debe coincidir con CITY_HERO_META)
 *   name          — nombre canónico EN (para API Viator y llamadas de backend)
 *   display       — nombre en español para UI
 *   country       — país en español
 *   continent     — continente (para filtros y gradientes fallback)
 *   moods         — array de moods para inspire panel (1–4 valores)
 *   vibes         — 2–3 descriptores cortos y específicos
 *   gradient      — CSS gradient para tarjetas (copia de CITY_HERO_META o
 *                   variación única por continente/región)
 *   accentColor   — oklch accent color
 *   providers     — proveedores con cobertura activa en esta ciudad
 *   aliases       — strings en minúsculas para el parser de texto
 *   featured      — true = aparece en la vista "sugeridos" (sin filtro de mood)
 *
 * Para agregar una ciudad nueva:
 *   1. Agrega una entrada en CITY_CATALOG con todos los campos.
 *   2. Si la ciudad tiene gradiente custom (CITY_HERO_META), cópialo aquí.
 *   3. Los aliases se usan en el queryParser — sé generoso con variantes.
 *
 * Multi-proveedor: cuando se active Civitatis / GetYourGuide / Klook,
 *   agregar el ProviderCode correspondiente en `providers`.
 *
 * Sprint 8A — V1 con ~110 destinos.
 */

// ─── Types ────────────────────────────────────────────────────────────────────

export type Continent =
  | "europa"
  | "asia"
  | "america_latina"
  | "america_norte"
  | "medio_oriente"
  | "africa"
  | "oceania";

export type MoodId =
  | "romantico"
  | "foodie"
  | "historia"
  | "arte"
  | "naturaleza"
  | "aventura"
  | "bienestar"
  | "fiesta";

export type ProviderCode = "viator" | "civitatis" | "getyourguide" | "klook";

export interface CatalogCity {
  /** Código identificador — IATA real o código RHEA para ciudades sin aeropuerto */
  iata:       string;
  /** Nombre canónico EN para llamadas de API (Viator, backend) */
  name:       string;
  /** Nombre en español para mostrar en UI */
  display:    string;
  country:    string;
  continent:  Continent;
  /** Sub-región geográfica (opcional, para filtros futuros) */
  region?:    string;
  moods:      MoodId[];
  /** 2–3 descriptores cortos y honestos */
  vibes:      string[];
  /** CSS linear-gradient para tarjeta de ciudad */
  gradient:   string;
  /** oklch accent — usado para textos, borders y chips de vibe */
  accentColor: string;
  /** Proveedores con cobertura activa */
  providers:  ProviderCode[];
  /** Aliases en minúsculas para resolución de texto libre */
  aliases:    string[];
  /** Aparece en vista "sugeridos" sin filtro de mood activo */
  featured?:  boolean;
  /** URL de foto de ciudad para las cards del inspire panel (Unsplash) */
  photo?:     string;
}

// ─── Gradientes fallback por continente ──────────────────────────────────────
// Usado como base para ciudades sin gradiente custom de CITY_HERO_META.
// Cada ciudad tiene una ligera variación en el hue para ser única.

const G = {
  europa:        (h1: number, h2: number) =>
    `linear-gradient(145deg, oklch(0.984 0.022 ${h1}) 0%, oklch(0.972 0.018 ${h2}) 100%)`,
  asia:          (h1: number, h2: number) =>
    `linear-gradient(145deg, oklch(0.982 0.018 ${h1}) 0%, oklch(0.970 0.014 ${h2}) 100%)`,
  america_latina: (h1: number, h2: number) =>
    `linear-gradient(145deg, oklch(0.985 0.026 ${h1}) 0%, oklch(0.973 0.022 ${h2}) 100%)`,
  america_norte: (h1: number, h2: number) =>
    `linear-gradient(145deg, oklch(0.982 0.014 ${h1}) 0%, oklch(0.970 0.010 ${h2}) 100%)`,
  medio_oriente: (h1: number, h2: number) =>
    `linear-gradient(145deg, oklch(0.985 0.024 ${h1}) 0%, oklch(0.973 0.020 ${h2}) 100%)`,
  africa:        (h1: number, h2: number) =>
    `linear-gradient(145deg, oklch(0.984 0.022 ${h1}) 0%, oklch(0.971 0.018 ${h2}) 100%)`,
  oceania:       (h1: number, h2: number) =>
    `linear-gradient(145deg, oklch(0.980 0.016 ${h1}) 0%, oklch(0.968 0.012 ${h2}) 100%)`,
} as const;

// ─── City catalog ─────────────────────────────────────────────────────────────

export const CITY_CATALOG: readonly CatalogCity[] = [

  // ══════════════════════════════════════════════════════════════════════════
  // EUROPA — España
  // ══════════════════════════════════════════════════════════════════════════

  {
    iata: "BCN", name: "Barcelona", display: "Barcelona", country: "España", continent: "europa", region: "Cataluña",
    moods: ["fiesta", "foodie", "arte"], vibes: ["Gaudí", "playa", "tapas"],
    gradient: "linear-gradient(145deg, oklch(0.985 0.026 56) 0%, oklch(0.972 0.022 40) 100%)",
    accentColor: "oklch(0.56 0.14 50)", providers: ["viator", "civitatis"],
    aliases: ["barcelona", "bcn", "barça", "barca"], featured: true,
    photo: "https://images.unsplash.com/photo-1539037116277-4db20889f2d4?w=600&h=400&fit=crop&auto=format&q=80",
  },
  {
    iata: "MAD", name: "Madrid", display: "Madrid", country: "España", continent: "europa",
    moods: ["fiesta", "arte", "foodie"], vibes: ["museos", "tapas", "nocturna"],
    gradient: "linear-gradient(145deg, oklch(0.984 0.026 30) 0%, oklch(0.972 0.022 22) 100%)",
    accentColor: "oklch(0.50 0.18 25)", providers: ["viator", "civitatis"],
    aliases: ["madrid", "mad"], featured: true,
    photo: "https://images.unsplash.com/photo-1539635278303-d4002c07eae3?w=600&h=400&fit=crop&auto=format&q=80",
  },
  {
    iata: "SEV", name: "Seville", display: "Sevilla", country: "España", continent: "europa", region: "Andalucía",
    moods: ["romantico", "historia", "fiesta"], vibes: ["flamenco", "tapas", "palacios"],
    gradient: "linear-gradient(145deg, oklch(0.985 0.026 46) 0%, oklch(0.972 0.022 38) 100%)",
    accentColor: "oklch(0.52 0.18 44)", providers: ["viator", "civitatis"],
    aliases: ["sevilla", "seville", "sev", "svq"], featured: true,
    photo: "https://images.unsplash.com/photo-1559521644-88bb65bf5e09?w=600&h=400&fit=crop&auto=format&q=80",
  },
  {
    iata: "GRA", name: "Granada", display: "Granada", country: "España", continent: "europa", region: "Andalucía",
    moods: ["historia", "romantico", "aventura"], vibes: ["Alhambra", "flamenco", "tapas gratis"],
    gradient: "linear-gradient(145deg, oklch(0.980 0.020 148) 0%, oklch(0.968 0.016 140) 100%)",
    accentColor: "oklch(0.42 0.12 145)", providers: ["viator", "civitatis"],
    aliases: ["granada", "gra", "grx"],
  },
  {
    iata: "COR", name: "Córdoba", display: "Córdoba", country: "España", continent: "europa", region: "Andalucía",
    moods: ["historia", "foodie"], vibes: ["Mezquita", "patios", "tapas"],
    gradient: "linear-gradient(145deg, oklch(0.984 0.024 44) 0%, oklch(0.972 0.020 36) 100%)",
    accentColor: "oklch(0.50 0.15 42)", providers: ["viator", "civitatis"],
    aliases: ["córdoba españa", "cordoba españa", "córdoba spain"],
  },
  {
    iata: "AGP", name: "Málaga", display: "Málaga", country: "España", continent: "europa", region: "Andalucía",
    moods: ["bienestar", "foodie", "aventura"], vibes: ["sol", "Picasso", "playa"],
    gradient: "linear-gradient(145deg, oklch(0.978 0.018 208) 0%, oklch(0.966 0.014 198) 100%)",
    accentColor: "oklch(0.44 0.10 205)", providers: ["viator", "civitatis"],
    aliases: ["málaga", "malaga", "agp"],
  },
  {
    iata: "CDZ", name: "Cádiz", display: "Cádiz", country: "España", continent: "europa", region: "Andalucía",
    moods: ["naturaleza", "historia", "aventura"], vibes: ["océano", "carnaval", "frescos"],
    gradient: "linear-gradient(145deg, oklch(0.976 0.016 200) 0%, oklch(0.964 0.012 190) 100%)",
    accentColor: "oklch(0.42 0.10 198)", providers: ["viator"],
    aliases: ["cádiz", "cadiz", "cdz"],
  },
  {
    iata: "TOL", name: "Toledo", display: "Toledo", country: "España", continent: "europa",
    moods: ["historia", "arte"], vibes: ["medieval", "espadas", "tres culturas"],
    gradient: "linear-gradient(145deg, oklch(0.983 0.022 36) 0%, oklch(0.970 0.018 28) 100%)",
    accentColor: "oklch(0.48 0.14 34)", providers: ["viator", "civitatis"],
    aliases: ["toledo", "tol"],
  },
  {
    iata: "VLC", name: "Valencia", display: "Valencia", country: "España", continent: "europa",
    moods: ["foodie", "fiesta", "bienestar"], vibes: ["paella", "Fallas", "playa"],
    gradient: G.europa(38, 30),
    accentColor: "oklch(0.52 0.16 36)", providers: ["viator", "civitatis"],
    aliases: ["valencia", "vlc"],
  },
  {
    iata: "BIO", name: "Bilbao", display: "Bilbao", country: "España", continent: "europa", region: "País Vasco",
    moods: ["arte", "foodie"], vibes: ["Guggenheim", "pintxos", "arquitectura"],
    gradient: G.europa(252, 244),
    accentColor: "oklch(0.44 0.10 250)", providers: ["viator", "civitatis"],
    aliases: ["bilbao", "bio", "san sebastián", "san sebastian", "donostia"],
  },
  {
    iata: "PMI", name: "Palma de Mallorca", display: "Mallorca", country: "España", continent: "europa",
    moods: ["bienestar", "naturaleza", "romantico"], vibes: ["cala", "vino", "montaña"],
    gradient: G.europa(196, 188),
    accentColor: "oklch(0.44 0.10 194)", providers: ["viator"],
    aliases: ["mallorca", "palma", "palma de mallorca", "pmi"],
  },
  {
    iata: "TFS", name: "Tenerife", display: "Tenerife", country: "España", continent: "africa",
    moods: ["naturaleza", "bienestar", "aventura"], vibes: ["Teide", "playa", "naturaleza"],
    gradient: G.africa(52, 44),
    accentColor: "oklch(0.50 0.14 50)", providers: ["viator"],
    aliases: ["tenerife", "tfs", "canarias"],
  },
  {
    iata: "LPA", name: "Las Palmas", display: "Gran Canaria", country: "España", continent: "africa",
    moods: ["bienestar", "naturaleza"], vibes: ["dunas", "playa", "surf"],
    gradient: G.africa(58, 50),
    accentColor: "oklch(0.50 0.14 56)", providers: ["viator"],
    aliases: ["gran canaria", "las palmas", "lpa"],
  },

  // ── Europa — Portugal ──────────────────────────────────────────────────────

  {
    iata: "LIS", name: "Lisbon", display: "Lisboa", country: "Portugal", continent: "europa",
    moods: ["bienestar", "foodie", "historia"], vibes: ["fado", "pasteles de Belém", "tranvías"],
    gradient: "linear-gradient(145deg, oklch(0.985 0.024 32) 0%, oklch(0.972 0.020 20) 100%)",
    accentColor: "oklch(0.52 0.13 28)", providers: ["viator", "civitatis"],
    aliases: ["lisboa", "lisbon", "lis"], featured: true,
    photo: "https://images.unsplash.com/photo-1558522195-e1201b090344?w=600&h=400&fit=crop&auto=format&q=80",
  },
  {
    iata: "OPO", name: "Porto", display: "Oporto", country: "Portugal", continent: "europa",
    moods: ["foodie", "bienestar", "historia"], vibes: ["vino de Oporto", "azulejos", "río Duero"],
    gradient: "linear-gradient(145deg, oklch(0.983 0.022 28) 0%, oklch(0.970 0.018 22) 100%)",
    accentColor: "oklch(0.50 0.14 24)", providers: ["viator", "civitatis"],
    aliases: ["oporto", "porto", "opo"],
  },
  {
    iata: "FAO", name: "Faro", display: "Algarve", country: "Portugal", continent: "europa", region: "Algarve",
    moods: ["naturaleza", "bienestar", "aventura"], vibes: ["acantilados", "cuevas", "playa"],
    gradient: "linear-gradient(145deg, oklch(0.978 0.018 195) 0%, oklch(0.966 0.014 185) 100%)",
    accentColor: "oklch(0.44 0.11 192)", providers: ["viator"],
    aliases: ["faro", "algarve", "fao"],
  },
  {
    iata: "CBM", name: "Coimbra", display: "Coimbra", country: "Portugal", continent: "europa",
    moods: ["historia", "bienestar"], vibes: ["universidad", "fado académico", "Mondego"],
    gradient: "linear-gradient(145deg, oklch(0.980 0.018 270) 0%, oklch(0.968 0.014 260) 100%)",
    accentColor: "oklch(0.44 0.10 265)", providers: ["viator"],
    aliases: ["coimbra", "cbm"],
  },
  {
    iata: "DOV", name: "Douro Valley", display: "Valle del Douro", country: "Portugal", continent: "europa",
    moods: ["naturaleza", "bienestar", "foodie"], vibes: ["viñedos", "río", "quintas"],
    gradient: "linear-gradient(145deg, oklch(0.980 0.020 148) 0%, oklch(0.968 0.016 138) 100%)",
    accentColor: "oklch(0.42 0.11 145)", providers: ["viator"],
    aliases: ["douro", "valle del douro", "duero portugal"],
  },

  // ── Europa — Francia ──────────────────────────────────────────────────────

  {
    iata: "CDG", name: "Paris", display: "París", country: "Francia", continent: "europa",
    moods: ["romantico", "arte", "foodie"], vibes: ["Torre Eiffel", "croissants", "museos"],
    gradient: "linear-gradient(145deg, oklch(0.978 0.018 242) 0%, oklch(0.966 0.014 228) 100%)",
    accentColor: "oklch(0.42 0.10 235)", providers: ["viator", "civitatis"],
    aliases: ["paris", "parís", "cdg", "ory"], featured: true,
    photo: "https://images.unsplash.com/photo-1499856374751-90a1aca43cf2?w=600&h=400&fit=crop&auto=format&q=80",
  },
  {
    iata: "NCE", name: "Nice", display: "Niza", country: "Francia", continent: "europa", region: "Costa Azul",
    moods: ["romantico", "bienestar", "foodie"], vibes: ["Riviera", "mar", "mercados"],
    gradient: G.europa(208, 200),
    accentColor: "oklch(0.44 0.10 206)", providers: ["viator"],
    aliases: ["niza", "nice", "nce", "costa azul", "côte d'azur"],
  },

  // ── Europa — Italia ────────────────────────────────────────────────────────

  {
    iata: "ROM", name: "Rome", display: "Roma", country: "Italia", continent: "europa",
    moods: ["historia", "foodie", "arte"], vibes: ["Coliseo", "pasta", "fuentes"],
    gradient: "linear-gradient(145deg, oklch(0.985 0.024 42) 0%, oklch(0.972 0.020 34) 100%)",
    accentColor: "oklch(0.52 0.14 40)", providers: ["viator", "civitatis"],
    aliases: ["roma", "rome", "rom", "fco"], featured: true,
    photo: "https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=600&h=400&fit=crop&auto=format&q=80",
  },
  {
    iata: "FLR", name: "Florence", display: "Florencia", country: "Italia", continent: "europa", region: "Toscana",
    moods: ["romantico", "arte", "historia"], vibes: ["Renacimiento", "Uffizi", "chianti"],
    gradient: "linear-gradient(145deg, oklch(0.984 0.024 50) 0%, oklch(0.972 0.020 42) 100%)",
    accentColor: "oklch(0.54 0.14 48)", providers: ["viator", "civitatis"],
    aliases: ["florencia", "florence", "flr", "firenze"], featured: true,
    photo: "https://images.unsplash.com/photo-1541370976299-4d24be23d87f?w=600&h=400&fit=crop&auto=format&q=80",
  },
  {
    iata: "VCE", name: "Venice", display: "Venecia", country: "Italia", continent: "europa",
    moods: ["romantico", "arte", "historia"], vibes: ["canales", "góndolas", "Carnaval"],
    gradient: "linear-gradient(145deg, oklch(0.978 0.018 208) 0%, oklch(0.966 0.014 198) 100%)",
    accentColor: "oklch(0.44 0.10 204)", providers: ["viator", "civitatis"],
    aliases: ["venecia", "venice", "vce", "venezia"], featured: true,
    photo: "https://images.unsplash.com/photo-1523906834658-6e24ef2386f9?w=600&h=400&fit=crop&auto=format&q=80",
  },
  {
    iata: "NAP", name: "Naples", display: "Nápoles", country: "Italia", continent: "europa",
    moods: ["foodie", "historia", "aventura"], vibes: ["pizza", "Vesubio", "Pompeya"],
    gradient: "linear-gradient(145deg, oklch(0.982 0.022 38) 0%, oklch(0.970 0.018 30) 100%)",
    accentColor: "oklch(0.52 0.15 36)", providers: ["viator"],
    aliases: ["nápoles", "napoles", "naples", "nap"],
  },
  {
    iata: "MXP", name: "Milan", display: "Milán", country: "Italia", continent: "europa",
    moods: ["arte", "fiesta"], vibes: ["moda", "Duomo", "aperitivo"],
    gradient: "linear-gradient(145deg, oklch(0.980 0.012 240) 0%, oklch(0.968 0.010 228) 100%)",
    accentColor: "oklch(0.40 0.08 232)", providers: ["viator", "civitatis"],
    aliases: ["milán", "milan", "milano", "mxp"],
  },

  // ── Europa — Benelux ──────────────────────────────────────────────────────

  {
    iata: "BGE", name: "Bruges", display: "Brujas", country: "Bélgica", continent: "europa",
    moods: ["romantico", "historia", "foodie"], vibes: ["canales", "cerveza", "chocolate"],
    gradient: "linear-gradient(145deg, oklch(0.980 0.016 265) 0%, oklch(0.968 0.012 255) 100%)",
    accentColor: "oklch(0.46 0.10 263)", providers: ["viator"],
    aliases: ["brujas", "bruges", "bge"],
  },
  {
    iata: "AMS", name: "Amsterdam", display: "Ámsterdam", country: "Países Bajos", continent: "europa",
    moods: ["arte", "bienestar", "fiesta"], vibes: ["canales", "Rijksmuseum", "bicicletas"],
    gradient: G.europa(256, 248),
    accentColor: "oklch(0.44 0.12 254)", providers: ["viator"],
    aliases: ["amsterdam", "ámsterdam", "ams", "holanda", "países bajos"],
  },
  {
    iata: "BRU", name: "Brussels", display: "Bruselas", country: "Bélgica", continent: "europa",
    moods: ["foodie", "historia", "arte"], vibes: ["waffles", "cómics", "EU"],
    gradient: G.europa(44, 36),
    accentColor: "oklch(0.50 0.14 42)", providers: ["viator"],
    aliases: ["bruselas", "brussels", "bru"],
  },

  // ── Europa — Alemania / Austria / Suiza ───────────────────────────────────

  {
    iata: "BER", name: "Berlin", display: "Berlín", country: "Alemania", continent: "europa",
    moods: ["arte", "fiesta", "historia"], vibes: ["muro", "clubs", "arte urbano"],
    gradient: G.europa(238, 230),
    accentColor: "oklch(0.42 0.10 236)", providers: ["viator"],
    aliases: ["berlín", "berlin", "ber"],
  },
  {
    iata: "MUC", name: "Munich", display: "Múnich", country: "Alemania", continent: "europa",
    moods: ["foodie", "fiesta", "historia"], vibes: ["cerveza", "Oktoberfest", "Alpes"],
    gradient: G.europa(40, 32),
    accentColor: "oklch(0.50 0.14 38)", providers: ["viator"],
    aliases: ["múnich", "munich", "muc"],
  },
  {
    iata: "VIE", name: "Vienna", display: "Viena", country: "Austria", continent: "europa",
    moods: ["arte", "historia", "bienestar"], vibes: ["ópera", "café imperial", "Klimt"],
    gradient: G.europa(272, 264),
    accentColor: "oklch(0.44 0.11 270)", providers: ["viator"],
    aliases: ["viena", "vienna", "vie"],
  },
  {
    iata: "ZRH", name: "Zurich", display: "Zúrich", country: "Suiza", continent: "europa",
    moods: ["bienestar", "naturaleza"], vibes: ["lago", "chocolate", "relojes"],
    gradient: G.europa(200, 192),
    accentColor: "oklch(0.44 0.10 198)", providers: ["viator"],
    aliases: ["zúrich", "zurich", "zrh"],
  },
  {
    iata: "GVA", name: "Geneva", display: "Ginebra", country: "Suiza", continent: "europa",
    moods: ["bienestar", "naturaleza"], vibes: ["Mont Blanc", "jet d'eau", "diplomacia"],
    gradient: G.europa(192, 184),
    accentColor: "oklch(0.44 0.10 190)", providers: ["viator"],
    aliases: ["ginebra", "geneva", "gva"],
  },

  // ── Europa — Chequia / Hungría / Polonia ──────────────────────────────────

  {
    iata: "PRG", name: "Prague", display: "Praga", country: "República Checa", continent: "europa",
    moods: ["romantico", "historia", "fiesta"], vibes: ["castillo", "cerveza artesanal", "Kafka"],
    gradient: G.europa(264, 256),
    accentColor: "oklch(0.44 0.11 262)", providers: ["viator"],
    aliases: ["praga", "prague", "prg"], featured: true,
    photo: "https://images.unsplash.com/photo-1541849546-216549ae216d?w=600&h=400&fit=crop&auto=format&q=80",
  },
  {
    iata: "BUD", name: "Budapest", display: "Budapest", country: "Hungría", continent: "europa",
    moods: ["romantico", "bienestar", "fiesta"], vibes: ["baños termales", "puentes", "nocturna"],
    gradient: G.europa(34, 26),
    accentColor: "oklch(0.50 0.16 32)", providers: ["viator"],
    aliases: ["budapest", "bud"],
  },
  {
    iata: "WAW", name: "Warsaw", display: "Varsovia", country: "Polonia", continent: "europa",
    moods: ["historia", "arte"], vibes: ["casco antiguo", "Chopin", "resiliencia"],
    gradient: G.europa(248, 240),
    accentColor: "oklch(0.44 0.10 246)", providers: ["viator"],
    aliases: ["varsovia", "warsaw", "waw"],
  },
  {
    iata: "KRK", name: "Krakow", display: "Cracovia", country: "Polonia", continent: "europa",
    moods: ["historia", "aventura"], vibes: ["Auschwitz", "castillo Wawel", "casco medieval"],
    gradient: G.europa(36, 28),
    accentColor: "oklch(0.48 0.13 34)", providers: ["viator"],
    aliases: ["cracovia", "krakow", "kraków", "krk"],
  },

  // ── Europa — Grecia ────────────────────────────────────────────────────────

  {
    iata: "ATH", name: "Athens", display: "Atenas", country: "Grecia", continent: "europa",
    moods: ["historia", "foodie", "aventura"], vibes: ["Acrópolis", "souvlaki", "islas cercanas"],
    gradient: G.europa(46, 38),
    accentColor: "oklch(0.50 0.14 44)", providers: ["viator"],
    aliases: ["atenas", "athens", "ath"],
  },

  // ── Europa — UK / Irlanda ──────────────────────────────────────────────────

  {
    iata: "LHR", name: "London", display: "Londres", country: "Reino Unido", continent: "europa",
    moods: ["arte", "historia", "foodie"], vibes: ["museos gratis", "pubs", "West End"],
    gradient: G.europa(245, 237),
    accentColor: "oklch(0.42 0.10 243)", providers: ["viator"],
    aliases: ["londres", "london", "lhr", "lgw", "stn"],
  },
  {
    iata: "EDI", name: "Edinburgh", display: "Edimburgo", country: "Reino Unido", continent: "europa",
    moods: ["historia", "aventura"], vibes: ["castillo", "whisky", "Highlands"],
    gradient: G.europa(220, 212),
    accentColor: "oklch(0.40 0.08 218)", providers: ["viator"],
    aliases: ["edimburgo", "edinburgh", "edi"],
  },
  {
    iata: "DUB", name: "Dublin", display: "Dublín", country: "Irlanda", continent: "europa",
    moods: ["fiesta", "historia"], vibes: ["pubs", "Guinness", "cliffs"],
    gradient: G.europa(155, 147),
    accentColor: "oklch(0.44 0.12 153)", providers: ["viator"],
    aliases: ["dublín", "dublin", "dub"],
  },

  // ── Europa — Nórdicos ──────────────────────────────────────────────────────

  {
    iata: "CPH", name: "Copenhagen", display: "Copenhague", country: "Dinamarca", continent: "europa",
    moods: ["bienestar", "foodie"], vibes: ["hygge", "noma", "bicicletas"],
    gradient: G.europa(196, 188),
    accentColor: "oklch(0.44 0.10 194)", providers: ["viator"],
    aliases: ["copenhague", "copenhagen", "cph"],
  },
  {
    iata: "ARN", name: "Stockholm", display: "Estocolmo", country: "Suecia", continent: "europa",
    moods: ["bienestar", "naturaleza"], vibes: ["archipiélago", "ABBA", "diseño"],
    gradient: G.europa(192, 184),
    accentColor: "oklch(0.42 0.10 190)", providers: ["viator"],
    aliases: ["estocolmo", "stockholm", "arn"],
  },
  {
    iata: "HEL", name: "Helsinki", display: "Helsinki", country: "Finlandia", continent: "europa",
    moods: ["bienestar", "naturaleza"], vibes: ["sauna", "aurora boreal", "diseño nórdico"],
    gradient: G.europa(230, 222),
    accentColor: "oklch(0.40 0.10 228)", providers: ["viator"],
    aliases: ["helsinki", "hel"],
  },

  // ── Europa — Turquía ───────────────────────────────────────────────────────

  {
    iata: "IST", name: "Istanbul", display: "Estambul", country: "Türkiye", continent: "europa",
    moods: ["historia", "foodie", "arte"], vibes: ["Hagia Sofía", "bazar", "bósforo"],
    gradient: G.europa(36, 28),
    accentColor: "oklch(0.50 0.16 34)", providers: ["viator"],
    aliases: ["estambul", "istanbul", "ist"], featured: true,
    photo: "https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?w=600&h=400&fit=crop&auto=format&q=80",
  },

  // ══════════════════════════════════════════════════════════════════════════
  // ASIA
  // ══════════════════════════════════════════════════════════════════════════

  {
    iata: "TYO", name: "Tokyo", display: "Tokio", country: "Japón", continent: "asia",
    moods: ["aventura", "foodie", "arte"], vibes: ["neon", "sushi", "tecnología"],
    gradient: "linear-gradient(145deg, oklch(0.975 0.016 290) 0%, oklch(0.962 0.025 274) 100%)",
    accentColor: "oklch(0.50 0.16 285)", providers: ["viator"],
    aliases: ["tokio", "tokyo", "tyo", "nrt", "hnd"], featured: true,
    photo: "https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=600&h=400&fit=crop&auto=format&q=80",
  },
  {
    iata: "KYO", name: "Kyoto", display: "Kioto", country: "Japón", continent: "asia",
    moods: ["romantico", "bienestar", "historia"], vibes: ["templos", "geishas", "sakura"],
    gradient: "linear-gradient(145deg, oklch(0.978 0.020 358) 0%, oklch(0.966 0.016 348) 100%)",
    accentColor: "oklch(0.46 0.12 356)", providers: ["viator"],
    aliases: ["kioto", "kyoto", "kyo"],
  },
  {
    iata: "OSA", name: "Osaka", display: "Osaka", country: "Japón", continent: "asia",
    moods: ["foodie", "fiesta", "aventura"], vibes: ["takoyaki", "Dotonbori", "Universal Studios"],
    gradient: "linear-gradient(145deg, oklch(0.982 0.022 50) 0%, oklch(0.970 0.018 40) 100%)",
    accentColor: "oklch(0.54 0.16 50)", providers: ["viator"],
    aliases: ["osaka", "osa"],
  },
  {
    iata: "HKN", name: "Hakone", display: "Hakone", country: "Japón", continent: "asia",
    moods: ["naturaleza", "bienestar"], vibes: ["onsen", "Monte Fuji", "ryokan"],
    gradient: "linear-gradient(145deg, oklch(0.980 0.016 160) 0%, oklch(0.968 0.012 152) 100%)",
    accentColor: "oklch(0.42 0.10 158)", providers: ["viator"],
    aliases: ["hakone", "hkn"],
  },
  {
    iata: "KNZ", name: "Kanazawa", display: "Kanazawa", country: "Japón", continent: "asia",
    moods: ["historia", "arte", "foodie"], vibes: ["jardín Kenroku-en", "mariscos", "geishas"],
    gradient: "linear-gradient(145deg, oklch(0.983 0.022 28) 0%, oklch(0.970 0.018 20) 100%)",
    accentColor: "oklch(0.50 0.14 24)", providers: ["viator"],
    aliases: ["kanazawa", "knz"],
  },
  {
    iata: "BKK", name: "Bangkok", display: "Bangkok", country: "Tailandia", continent: "asia",
    moods: ["foodie", "fiesta", "aventura"], vibes: ["templos", "street food", "tuk-tuk"],
    gradient: G.asia(46, 38),
    accentColor: "oklch(0.50 0.16 44)", providers: ["viator", "klook"],
    aliases: ["bangkok", "bkk", "tailandia"], featured: true,
    photo: "https://images.unsplash.com/photo-1563492065599-3520f775eeed?w=600&h=400&fit=crop&auto=format&q=80",
  },
  {
    iata: "SIN", name: "Singapore", display: "Singapur", country: "Singapur", continent: "asia",
    moods: ["foodie", "arte", "bienestar"], vibes: ["hawker centres", "Gardens by the Bay", "eficiencia"],
    gradient: G.asia(55, 47),
    accentColor: "oklch(0.50 0.15 53)", providers: ["viator", "klook"],
    aliases: ["singapur", "singapore"],
  },
  {
    iata: "DPS", name: "Bali", display: "Bali", country: "Indonesia", continent: "asia",
    moods: ["naturaleza", "bienestar", "romantico"], vibes: ["arrozales", "templos", "surf"],
    gradient: G.asia(155, 147),
    accentColor: "oklch(0.46 0.14 153)", providers: ["viator", "klook"],
    aliases: ["bali", "dps"], featured: true,
    photo: "https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=600&h=400&fit=crop&auto=format&q=80",
  },
  {
    iata: "HAN", name: "Hanoi", display: "Hanói", country: "Vietnam", continent: "asia",
    moods: ["historia", "foodie", "aventura"], vibes: ["pho", "casco antiguo", "bahía Halong"],
    gradient: G.asia(158, 150),
    accentColor: "oklch(0.44 0.12 156)", providers: ["viator"],
    aliases: ["hanói", "hanoi", "han"],
  },
  {
    iata: "SGN", name: "Ho Chi Minh City", display: "Ho Chi Minh", country: "Vietnam", continent: "asia",
    moods: ["foodie", "aventura", "historia"], vibes: ["banh mi", "motos", "túneles Cu Chi"],
    gradient: G.asia(50, 42),
    accentColor: "oklch(0.50 0.15 48)", providers: ["viator"],
    aliases: ["ho chi minh", "saigón", "saigon", "sgn"],
  },
  {
    iata: "KUL", name: "Kuala Lumpur", display: "Kuala Lumpur", country: "Malasia", continent: "asia",
    moods: ["foodie", "arte", "aventura"], vibes: ["Petronas", "dim sum", "Batu Caves"],
    gradient: G.asia(270, 262),
    accentColor: "oklch(0.44 0.12 268)", providers: ["viator", "klook"],
    aliases: ["kuala lumpur", "kl", "kul"],
  },
  {
    iata: "DEL", name: "Delhi", display: "Delhi", country: "India", continent: "asia",
    moods: ["historia", "foodie", "aventura"], vibes: ["Taj Mahal", "curry", "mercados"],
    gradient: G.asia(42, 34),
    accentColor: "oklch(0.50 0.16 40)", providers: ["viator"],
    aliases: ["delhi", "nueva delhi", "new delhi"],
  },
  {
    iata: "BOM", name: "Mumbai", display: "Bombay", country: "India", continent: "asia",
    moods: ["foodie", "arte", "fiesta"], vibes: ["Bollywood", "dabbawala", "Gateway of India"],
    gradient: G.asia(38, 30),
    accentColor: "oklch(0.50 0.16 36)", providers: ["viator"],
    aliases: ["bombay", "mumbai", "bom"],
  },

  // ══════════════════════════════════════════════════════════════════════════
  // OCEANÍA
  // ══════════════════════════════════════════════════════════════════════════

  {
    iata: "SYD", name: "Sydney", display: "Sídney", country: "Australia", continent: "oceania",
    moods: ["aventura", "naturaleza", "fiesta"], vibes: ["Opera House", "Bondi Beach", "vida al aire libre"],
    gradient: G.oceania(192, 184),
    accentColor: "oklch(0.44 0.12 190)", providers: ["viator"],
    aliases: ["sídney", "sydney", "syd"],
  },

  // ══════════════════════════════════════════════════════════════════════════
  // MEDIO ORIENTE
  // ══════════════════════════════════════════════════════════════════════════

  {
    iata: "DXB", name: "Dubai", display: "Dubái", country: "EAU", continent: "medio_oriente",
    moods: ["fiesta", "bienestar", "aventura"], vibes: ["rascacielos", "desierto", "lujo"],
    gradient: G.medio_oriente(64, 56),
    accentColor: "oklch(0.52 0.16 62)", providers: ["viator"],
    aliases: ["dubái", "dubai", "dxb"], featured: true,
    photo: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=600&h=400&fit=crop&auto=format&q=80",
  },
  {
    iata: "DOH", name: "Doha", display: "Doha", country: "Qatar", continent: "medio_oriente",
    moods: ["historia", "arte"], vibes: ["Souq Waqif", "desierto", "museos"],
    gradient: G.medio_oriente(60, 52),
    accentColor: "oklch(0.50 0.14 58)", providers: ["viator"],
    aliases: ["doha", "doh"],
  },
  {
    iata: "AMM", name: "Amman", display: "Amán", country: "Jordania", continent: "medio_oriente",
    moods: ["historia", "aventura"], vibes: ["Petra", "desierto Wadi Rum", "hospitalidad beduina"],
    gradient: G.medio_oriente(56, 48),
    accentColor: "oklch(0.50 0.14 54)", providers: ["viator"],
    aliases: ["amán", "amman", "amm", "jordania", "petra"],
  },

  // ══════════════════════════════════════════════════════════════════════════
  // ÁFRICA
  // ══════════════════════════════════════════════════════════════════════════

  {
    iata: "RAK", name: "Marrakech", display: "Marrakech", country: "Marruecos", continent: "africa",
    moods: ["historia", "foodie", "aventura"], vibes: ["medina", "tagine", "desierto del Sáhara"],
    gradient: G.africa(52, 44),
    accentColor: "oklch(0.50 0.16 50)", providers: ["viator"],
    aliases: ["marrakech", "marrakesh", "rak", "marruecos"], featured: true,
    photo: "https://images.unsplash.com/photo-1539020140153-e479b8c22e70?w=600&h=400&fit=crop&auto=format&q=80",
  },
  {
    iata: "CAI", name: "Cairo", display: "El Cairo", country: "Egipto", continent: "africa",
    moods: ["historia", "aventura"], vibes: ["pirámides", "Nilo", "bazar Kan el-Jalili"],
    gradient: G.africa(60, 52),
    accentColor: "oklch(0.52 0.16 58)", providers: ["viator"],
    aliases: ["el cairo", "cairo", "cai", "egipto"],
  },
  {
    iata: "CPT", name: "Cape Town", display: "Ciudad del Cabo", country: "Sudáfrica", continent: "africa",
    moods: ["naturaleza", "aventura", "bienestar"], vibes: ["Table Mountain", "vinos", "pingüinos"],
    gradient: G.africa(188, 180),
    accentColor: "oklch(0.46 0.12 186)", providers: ["viator"],
    aliases: ["ciudad del cabo", "cape town", "cpt"],
  },
  {
    iata: "JNB", name: "Johannesburg", display: "Johannesburgo", country: "Sudáfrica", continent: "africa",
    moods: ["aventura", "historia"], vibes: ["safari", "Apartheid Museum", "Soweto"],
    gradient: G.africa(46, 38),
    accentColor: "oklch(0.48 0.14 44)", providers: ["viator"],
    aliases: ["johannesburgo", "johannesburg", "jnb"],
  },

  // ══════════════════════════════════════════════════════════════════════════
  // LATINOAMÉRICA
  // ══════════════════════════════════════════════════════════════════════════

  {
    iata: "MEX", name: "Ciudad de México", display: "CDMX", country: "México", continent: "america_latina",
    moods: ["foodie", "arte", "fiesta"], vibes: ["tacos", "murales", "Frida Kahlo"],
    gradient: "linear-gradient(145deg, oklch(0.985 0.028 52) 0%, oklch(0.972 0.026 64) 100%)",
    accentColor: "oklch(0.54 0.16 48)", providers: ["viator", "civitatis"],
    // "mexico city"/"méxico city" (English-language query support) added
    // deliberately WITHOUT a bare "mexico"/"méxico" alias — a plain country
    // query must stay unsupported/country-scope in the frontend, never
    // silently resolve to the city (Marketplace Filters V2, task 1).
    aliases: ["cdmx", "ciudad de méxico", "ciudad de mexico", "mexico city", "méxico city", "méx", "mex", "nlu"], featured: true,
    photo: "https://images.unsplash.com/photo-1518638150340-f706e86654de?w=600&h=400&fit=crop&auto=format&q=80",
  },
  {
    iata: "GDL", name: "Guadalajara", display: "Guadalajara", country: "México", continent: "america_latina",
    moods: ["foodie", "historia", "fiesta"], vibes: ["tequila", "mariachi", "tapalpa"],
    gradient: G.america_latina(44, 36),
    accentColor: "oklch(0.52 0.16 42)", providers: ["viator"],
    aliases: ["guadalajara", "gdl"],
  },
  {
    iata: "CUN", name: "Cancún", display: "Cancún", country: "México", continent: "america_latina",
    moods: ["aventura", "naturaleza", "bienestar"], vibes: ["cenotes", "Chichén Itzá", "arrecife"],
    gradient: G.america_latina(192, 184),
    accentColor: "oklch(0.44 0.14 190)", providers: ["viator"],
    aliases: ["cancún", "cancun", "cun", "riviera maya"],
  },
  {
    iata: "PVR", name: "Puerto Vallarta", display: "Puerto Vallarta", country: "México", continent: "america_latina",
    moods: ["bienestar", "naturaleza", "romantico"], vibes: ["bahía Banderas", "malecón", "joyas de agua"],
    gradient: G.america_latina(200, 192),
    accentColor: "oklch(0.44 0.12 198)", providers: ["viator"],
    aliases: ["puerto vallarta", "pvr"],
  },
  {
    iata: "BOG", name: "Bogotá", display: "Bogotá", country: "Colombia", continent: "america_latina",
    moods: ["arte", "foodie", "historia"], vibes: ["Monserrate", "Botero", "café"],
    gradient: G.america_latina(155, 147),
    accentColor: "oklch(0.46 0.14 153)", providers: ["viator", "civitatis"],
    aliases: ["bogotá", "bogota", "bog"], featured: true,
    photo: "https://images.unsplash.com/photo-1597010878688-3a9b4cc76ef0?w=600&h=400&fit=crop&auto=format&q=80",
  },
  {
    iata: "MDE", name: "Medellín", display: "Medellín", country: "Colombia", continent: "america_latina",
    moods: ["fiesta", "aventura", "arte"], vibes: ["metrocable", "metro", "orquídeas"],
    gradient: G.america_latina(148, 140),
    accentColor: "oklch(0.46 0.14 146)", providers: ["viator"],
    aliases: ["medellín", "medellin", "mde"],
  },
  {
    iata: "CTG", name: "Cartagena", display: "Cartagena", country: "Colombia", continent: "america_latina",
    moods: ["romantico", "historia", "naturaleza"], vibes: ["ciudad amurallada", "Caribe", "palacio"],
    gradient: G.america_latina(44, 36),
    accentColor: "oklch(0.52 0.16 42)", providers: ["viator"],
    aliases: ["cartagena", "ctg", "cartagena colombia"],
  },
  {
    iata: "LIM", name: "Lima", display: "Lima", country: "Perú", continent: "america_latina",
    moods: ["foodie", "historia", "aventura"], vibes: ["ceviche", "Huaca Pucllana", "Miraflores"],
    gradient: G.america_latina(40, 32),
    accentColor: "oklch(0.50 0.15 38)", providers: ["viator", "civitatis"],
    aliases: ["lima", "lim"], featured: true,
    photo: "https://images.unsplash.com/photo-1531968455001-5c5272a41129?w=600&h=400&fit=crop&auto=format&q=80",
  },
  {
    iata: "CUZ", name: "Cusco", display: "Cusco", country: "Perú", continent: "america_latina",
    moods: ["historia", "aventura", "naturaleza"], vibes: ["Machu Picchu", "inca", "altitud"],
    gradient: G.america_latina(36, 28),
    accentColor: "oklch(0.50 0.16 34)", providers: ["viator", "civitatis"],
    aliases: ["cusco", "cuzco", "cuz", "machu picchu"],
  },
  {
    iata: "EZE", name: "Buenos Aires", display: "Buenos Aires", country: "Argentina", continent: "america_latina",
    moods: ["arte", "foodie", "fiesta"], vibes: ["tango", "asado", "Boca"],
    gradient: G.america_latina(210, 202),
    accentColor: "oklch(0.44 0.12 208)", providers: ["viator", "civitatis"],
    aliases: ["buenos aires", "eze", "aep"], featured: true,
    photo: "https://images.unsplash.com/photo-1589909202802-8f4aadce1849?w=600&h=400&fit=crop&auto=format&q=80",
  },
  {
    iata: "MDZ", name: "Mendoza", display: "Mendoza", country: "Argentina", continent: "america_latina",
    moods: ["foodie", "naturaleza", "bienestar"], vibes: ["Malbec", "Andes", "bodegas"],
    gradient: G.america_latina(36, 28),
    accentColor: "oklch(0.50 0.14 34)", providers: ["viator"],
    aliases: ["mendoza", "mdz"],
  },
  {
    iata: "SCL", name: "Santiago", display: "Santiago", country: "Chile", continent: "america_latina",
    moods: ["foodie", "aventura", "naturaleza"], vibes: ["Andes", "viñedos", "empanadas"],
    gradient: G.america_latina(196, 188),
    accentColor: "oklch(0.44 0.12 194)", providers: ["viator", "civitatis"],
    aliases: ["santiago", "santiago de chile", "scl"],
  },
  {
    iata: "GRU", name: "São Paulo", display: "São Paulo", country: "Brasil", continent: "america_latina",
    moods: ["arte", "foodie", "fiesta"], vibes: ["gastronomía", "Vila Madalena", "museos"],
    gradient: G.america_latina(155, 147),
    accentColor: "oklch(0.46 0.14 153)", providers: ["viator"],
    aliases: ["são paulo", "sao paulo", "gru", "cgh"],
  },
  {
    iata: "GIG", name: "Rio de Janeiro", display: "Río de Janeiro", country: "Brasil", continent: "america_latina",
    moods: ["fiesta", "naturaleza", "aventura"], vibes: ["Cristo Redentor", "carnaval", "playa"],
    gradient: G.america_latina(148, 140),
    accentColor: "oklch(0.46 0.14 146)", providers: ["viator"],
    aliases: ["río de janeiro", "rio de janeiro"], featured: true,
    photo: "https://images.unsplash.com/photo-1483729558449-99ef09a8c325?w=600&h=400&fit=crop&auto=format&q=80",
  },
  {
    iata: "SSA", name: "Salvador", display: "Salvador", country: "Brasil", continent: "america_latina",
    moods: ["fiesta", "historia", "foodie"], vibes: ["Pelourinho", "acarajé", "candomblé"],
    gradient: G.america_latina(44, 36),
    accentColor: "oklch(0.52 0.16 42)", providers: ["viator"],
    aliases: ["salvador", "salvador bahia", "ssa"],
  },
  {
    iata: "MVD", name: "Montevideo", display: "Montevideo", country: "Uruguay", continent: "america_latina",
    moods: ["bienestar", "foodie"], vibes: ["rambla", "asado", "mate"],
    gradient: G.america_latina(204, 196),
    accentColor: "oklch(0.44 0.12 202)", providers: ["viator"],
    aliases: ["montevideo", "mvd"],
  },
  {
    iata: "HAV", name: "Havana", display: "La Habana", country: "Cuba", continent: "america_latina",
    moods: ["historia", "arte", "fiesta"], vibes: ["autos clásicos", "son cubano", "malecón"],
    gradient: G.america_latina(42, 34),
    accentColor: "oklch(0.52 0.16 40)", providers: ["viator"],
    aliases: ["la habana", "havana", "hav", "cuba"],
  },
  {
    iata: "UIO", name: "Quito", display: "Quito", country: "Ecuador", continent: "america_latina",
    moods: ["historia", "naturaleza", "aventura"], vibes: ["casco colonial", "volcanes", "Galápagos"],
    gradient: G.america_latina(148, 140),
    accentColor: "oklch(0.46 0.14 146)", providers: ["viator"],
    aliases: ["quito", "uio"],
  },
  {
    iata: "CUE", name: "Cuenca", display: "Cuenca", country: "Ecuador", continent: "america_latina",
    moods: ["historia", "bienestar"], vibes: ["patrimonio UNESCO", "sombreros", "ríos"],
    gradient: G.america_latina(40, 32),
    accentColor: "oklch(0.50 0.14 38)", providers: ["viator"],
    aliases: ["cuenca", "cuenca ecuador", "cue"],
  },
  {
    iata: "SJO", name: "San José", display: "San José", country: "Costa Rica", continent: "america_latina",
    moods: ["naturaleza", "aventura"], vibes: ["bosque nuboso", "pura vida", "tortugas"],
    gradient: G.america_latina(152, 144),
    accentColor: "oklch(0.46 0.14 150)", providers: ["viator"],
    aliases: ["san josé", "san jose", "sjo", "costa rica"],
  },
  {
    iata: "PTY", name: "Panama City", display: "Ciudad de Panamá", country: "Panamá", continent: "america_latina",
    moods: ["historia", "aventura"], vibes: ["canal", "Casco Viejo", "biodiversidad"],
    gradient: G.america_latina(192, 184),
    accentColor: "oklch(0.44 0.12 190)", providers: ["viator"],
    // Supply Coverage V2.1: bare "panamá"/"panama" added deliberately, unlike
    // Mexico's bare "mexico"/"méxico" (which stays unsupported/country-scope
    // on purpose — see the MEX entry above). Panama City is the ONLY Panama
    // entry in this catalog, so there is no other city a bare "Panamá" query
    // could ambiguously mean; collapsing it straight to PTY is the more
    // useful, still-honest default. Mirrors the backend fix in
    // engine/cities/__init__.py (Supply Coverage + Destination Resolution V2).
    aliases: [
      "ciudad de panamá", "ciudad de panama", "panama city", "pty",
      "canal de panamá", "canal de panama", "panamá", "panama",
    ],
  },

  // ══════════════════════════════════════════════════════════════════════════
  // NORTEAMÉRICA
  // ══════════════════════════════════════════════════════════════════════════

  {
    iata: "JFK", name: "New York", display: "Nueva York", country: "Estados Unidos", continent: "america_norte",
    moods: ["arte", "foodie", "fiesta"], vibes: ["Manhattan", "Broadway", "bagels"],
    gradient: G.america_norte(218, 210),
    accentColor: "oklch(0.42 0.10 216)", providers: ["viator"],
    aliases: ["nueva york", "new york", "nyc", "jfk", "ewr", "lga"], featured: true,
    photo: "https://images.unsplash.com/photo-1534430480872-3498386e7856?w=600&h=400&fit=crop&auto=format&q=80",
  },
  {
    iata: "LAX", name: "Los Angeles", display: "Los Ángeles", country: "Estados Unidos", continent: "america_norte",
    moods: ["fiesta", "arte", "bienestar"], vibes: ["Hollywood", "playa", "avocado toast"],
    gradient: G.america_norte(38, 30),
    accentColor: "oklch(0.50 0.14 36)", providers: ["viator"],
    aliases: ["los ángeles", "los angeles", "lax"],
  },
  {
    iata: "SFO", name: "San Francisco", display: "San Francisco", country: "Estados Unidos", continent: "america_norte",
    moods: ["foodie", "aventura", "arte"], vibes: ["Golden Gate", "brunch", "tech"],
    gradient: G.america_norte(200, 192),
    accentColor: "oklch(0.44 0.10 198)", providers: ["viator"],
    aliases: ["san francisco", "sf", "sfo"],
  },
  {
    iata: "MIA", name: "Miami", display: "Miami", country: "Estados Unidos", continent: "america_norte",
    moods: ["fiesta", "bienestar", "foodie"], vibes: ["Art Deco", "Cuban food", "playa"],
    gradient: G.america_norte(192, 184),
    accentColor: "oklch(0.44 0.12 190)", providers: ["viator"],
    aliases: ["miami", "mia"],
  },
  {
    iata: "ORD", name: "Chicago", display: "Chicago", country: "Estados Unidos", continent: "america_norte",
    moods: ["arte", "foodie", "historia"], vibes: ["arquitectura", "deep dish", "jazz"],
    gradient: G.america_norte(212, 204),
    accentColor: "oklch(0.42 0.10 210)", providers: ["viator"],
    aliases: ["chicago", "ord"],
  },
  {
    iata: "YYZ", name: "Toronto", display: "Toronto", country: "Canadá", continent: "america_norte",
    moods: ["arte", "foodie"], vibes: ["CN Tower", "multicultural", "Niágara cercano"],
    gradient: G.america_norte(216, 208),
    accentColor: "oklch(0.42 0.10 214)", providers: ["viator"],
    aliases: ["toronto", "yyz"],
  },

  // ══════════════════════════════════════════════════════════════════════════
  // EUROPA — nuevas ciudades
  // ══════════════════════════════════════════════════════════════════════════

  {
    iata: "EAS", name: "San Sebastian", display: "San Sebastián", country: "España", continent: "europa", region: "País Vasco",
    moods: ["foodie", "bienestar", "naturaleza"], vibes: ["pintxos", "surf", "estrella Michelin"],
    gradient: G.europa(192, 184),
    accentColor: "oklch(0.44 0.10 190)", providers: ["viator", "civitatis"],
    aliases: ["san sebastián", "san sebastian", "donostia", "eas"],
  },
  {
    iata: "IBZ", name: "Ibiza", display: "Ibiza", country: "España", continent: "europa", region: "Islas Baleares",
    moods: ["fiesta", "bienestar", "naturaleza"], vibes: ["electrónica", "calas", "puestas de sol"],
    gradient: G.europa(230, 220),
    accentColor: "oklch(0.46 0.12 228)", providers: ["viator", "civitatis"],
    aliases: ["ibiza", "eivissa", "ibz"],
  },
  {
    iata: "ACE", name: "Lanzarote", display: "Lanzarote", country: "España", continent: "africa",
    moods: ["naturaleza", "bienestar", "aventura"], vibes: ["volcanes", "playas negras", "surf"],
    gradient: G.africa(44, 36),
    accentColor: "oklch(0.50 0.14 42)", providers: ["viator"],
    aliases: ["lanzarote", "ace"],
  },
  {
    iata: "PMO", name: "Palermo", display: "Palermo", country: "Italia", continent: "europa", region: "Sicilia",
    moods: ["foodie", "historia", "aventura"], vibes: ["mercados", "arancini", "arte árabe-normando"],
    gradient: G.europa(42, 34),
    accentColor: "oklch(0.52 0.16 40)", providers: ["viator"],
    aliases: ["palermo", "sicilia", "sicily", "pmo"],
  },
  {
    iata: "TRN", name: "Turin", display: "Turín", country: "Italia", continent: "europa",
    moods: ["foodie", "arte", "historia"], vibes: ["chocolate", "Juventus", "Saboya"],
    gradient: G.europa(46, 38),
    accentColor: "oklch(0.50 0.14 44)", providers: ["viator"],
    aliases: ["turín", "turin", "trn"],
  },
  {
    iata: "BLQ", name: "Bologna", display: "Bolonia", country: "Italia", continent: "europa",
    moods: ["foodie", "historia", "arte"], vibes: ["pasta original", "soportales", "la grassa"],
    gradient: G.europa(34, 26),
    accentColor: "oklch(0.52 0.18 32)", providers: ["viator"],
    aliases: ["bolonia", "bologna", "blq"],
  },
  {
    iata: "VRN", name: "Verona", display: "Verona", country: "Italia", continent: "europa",
    moods: ["romantico", "historia", "arte"], vibes: ["Romeo y Julieta", "ópera", "vino"],
    gradient: G.europa(30, 22),
    accentColor: "oklch(0.50 0.16 28)", providers: ["viator"],
    aliases: ["verona", "vrn"],
  },
  {
    iata: "GNO", name: "Genoa", display: "Génova", country: "Italia", continent: "europa", region: "Liguria",
    moods: ["historia", "foodie", "naturaleza"], vibes: ["Cinque Terre base", "pesto", "caruggi"],
    gradient: G.europa(196, 188),
    accentColor: "oklch(0.44 0.10 194)", providers: ["viator"],
    aliases: ["génova", "genova", "genoa", "cinque terre", "goa liguria"],
  },
  {
    iata: "LYS", name: "Lyon", display: "Lyon", country: "Francia", continent: "europa",
    moods: ["foodie", "arte", "historia"], vibes: ["gastronomía", "bouchon", "seda"],
    gradient: G.europa(40, 32),
    accentColor: "oklch(0.52 0.16 38)", providers: ["viator"],
    aliases: ["lyon", "lys"],
  },
  {
    iata: "BOD", name: "Bordeaux", display: "Burdeos", country: "Francia", continent: "europa",
    moods: ["foodie", "romantico", "bienestar"], vibes: ["vino", "art déco", "Médoc"],
    gradient: G.europa(28, 20),
    accentColor: "oklch(0.48 0.16 26)", providers: ["viator"],
    aliases: ["burdeos", "bordeaux", "bod"],
  },
  {
    iata: "MRS", name: "Marseille", display: "Marsella", country: "Francia", continent: "europa",
    moods: ["foodie", "naturaleza", "aventura"], vibes: ["bouillabaisse", "Calanques", "MuCEM"],
    gradient: G.europa(210, 202),
    accentColor: "oklch(0.44 0.10 208)", providers: ["viator"],
    aliases: ["marsella", "marseille", "marseilla", "mrs"],
  },
  {
    iata: "SXB", name: "Strasbourg", display: "Estrasburgo", country: "Francia", continent: "europa", region: "Alsacia",
    moods: ["historia", "foodie", "romantico"], vibes: ["alsaciano", "navidad", "canal"],
    gradient: G.europa(50, 42),
    accentColor: "oklch(0.52 0.14 48)", providers: ["viator"],
    aliases: ["estrasburgo", "strasbourg", "sxb", "alsacia"],
  },
  {
    iata: "OSL", name: "Oslo", display: "Oslo", country: "Noruega", continent: "europa",
    moods: ["naturaleza", "arte", "bienestar"], vibes: ["fiordos", "vikingos", "diseño"],
    gradient: G.europa(215, 205),
    accentColor: "oklch(0.44 0.10 212)", providers: ["viator"],
    aliases: ["oslo", "osl"],
  },
  {
    iata: "KEF", name: "Reykjavik", display: "Reikiavik", country: "Islandia", continent: "europa",
    moods: ["naturaleza", "aventura", "bienestar"], vibes: ["aurora boreal", "géiseres", "termas"],
    gradient: "linear-gradient(145deg, oklch(0.978 0.018 220) 0%, oklch(0.966 0.014 212) 100%)",
    accentColor: "oklch(0.44 0.12 218)", providers: ["viator"],
    aliases: ["reikiavik", "reykjavik", "islandia", "iceland", "kef"],
  },
  {
    iata: "DBV", name: "Dubrovnik", display: "Dubrovnik", country: "Croacia", continent: "europa",
    moods: ["romantico", "historia", "naturaleza"], vibes: ["murallas", "Adriático", "Game of Thrones"],
    gradient: G.europa(196, 188),
    accentColor: "oklch(0.44 0.10 194)", providers: ["viator"],
    aliases: ["dubrovnik", "dbv", "croacia coast"],
  },
  {
    iata: "SPU", name: "Split", display: "Split", country: "Croacia", continent: "europa",
    moods: ["historia", "naturaleza", "aventura"], vibes: ["Diocleciano", "islas cercanas", "vida local"],
    gradient: G.europa(202, 194),
    accentColor: "oklch(0.44 0.10 200)", providers: ["viator"],
    aliases: ["split", "spu", "croacia"],
  },
  {
    iata: "TBS", name: "Tbilisi", display: "Tiflis", country: "Georgia", continent: "europa",
    moods: ["foodie", "historia", "aventura"], vibes: ["vino qvevri", "baños azufre", "caulcásica"],
    gradient: G.europa(36, 28),
    accentColor: "oklch(0.52 0.16 34)", providers: ["viator"],
    aliases: ["tiflis", "tbilisi", "georgia", "tbs"],
  },
  {
    iata: "TLL", name: "Tallinn", display: "Tallin", country: "Estonia", continent: "europa",
    moods: ["historia", "arte", "romantico"], vibes: ["medieval", "digital", "báltico"],
    gradient: G.europa(212, 204),
    accentColor: "oklch(0.44 0.10 210)", providers: ["viator"],
    aliases: ["tallin", "tallinn", "estonia", "tll"],
  },
  {
    iata: "JTR", name: "Santorini", display: "Santorini", country: "Grecia", continent: "europa",
    moods: ["romantico", "bienestar", "foodie"], vibes: ["caldera", "atardeceres", "vino volcánico"],
    gradient: "linear-gradient(145deg, oklch(0.982 0.018 222) 0%, oklch(0.970 0.014 214) 100%)",
    accentColor: "oklch(0.46 0.12 220)", providers: ["viator"],
    aliases: ["santorini", "thira", "jtr"],
  },
  {
    iata: "JMK", name: "Mykonos", display: "Mykonos", country: "Grecia", continent: "europa",
    moods: ["fiesta", "bienestar", "romantico"], vibes: ["molinos", "playas", "cosmopolita"],
    gradient: G.europa(216, 208),
    accentColor: "oklch(0.46 0.10 214)", providers: ["viator"],
    aliases: ["mykonos", "jmk"],
  },
  {
    iata: "SKG", name: "Thessaloniki", display: "Tesalónica", country: "Grecia", continent: "europa",
    moods: ["foodie", "historia", "arte"], vibes: ["gastronomía griega", "bizancio", "mercado"],
    gradient: G.europa(42, 34),
    accentColor: "oklch(0.50 0.14 40)", providers: ["viator"],
    aliases: ["tesalónica", "thessaloniki", "skg"],
  },

  // ══════════════════════════════════════════════════════════════════════════
  // ASIA — nuevas ciudades
  // ══════════════════════════════════════════════════════════════════════════

  {
    iata: "ICN", name: "Seoul", display: "Seúl", country: "Corea del Sur", continent: "asia",
    moods: ["foodie", "arte", "fiesta"], vibes: ["K-pop", "palacio Gyeongbokgung", "street food"],
    gradient: G.asia(336, 328),
    accentColor: "oklch(0.50 0.16 334)", providers: ["viator", "klook"],
    aliases: ["seúl", "seoul", "corea", "korea", "icn"], featured: true,
    photo: "https://images.unsplash.com/photo-1517154421773-0529f29ea451?w=600&h=400&fit=crop&auto=format&q=80",
  },
  {
    iata: "PUS", name: "Busan", display: "Busan", country: "Corea del Sur", continent: "asia",
    moods: ["foodie", "naturaleza", "bienestar"], vibes: ["mariscos", "playa Haeundae", "mercado Jagalchi"],
    gradient: G.asia(196, 188),
    accentColor: "oklch(0.44 0.10 194)", providers: ["viator", "klook"],
    aliases: ["busan", "pus"],
  },
  {
    iata: "PEK", name: "Beijing", display: "Pekín", country: "China", continent: "asia",
    moods: ["historia", "arte", "aventura"], vibes: ["Gran Muralla", "Ciudad Prohibida", "pato pekinés"],
    gradient: G.asia(28, 20),
    accentColor: "oklch(0.50 0.18 26)", providers: ["viator"],
    aliases: ["pekín", "beijing", "pekin", "pek"],
  },
  {
    iata: "PVG", name: "Shanghai", display: "Shanghái", country: "China", continent: "asia",
    moods: ["arte", "foodie", "fiesta"], vibes: ["Bund", "dim sum", "rascacielos"],
    gradient: G.asia(224, 216),
    accentColor: "oklch(0.44 0.10 222)", providers: ["viator"],
    aliases: ["shanghái", "shanghai", "pvg"],
  },
  {
    iata: "HKG", name: "Hong Kong", display: "Hong Kong", country: "China", continent: "asia",
    moods: ["foodie", "aventura", "fiesta"], vibes: ["dim sum", "Victoria Peak", "noche skyline"],
    gradient: G.asia(334, 326),
    accentColor: "oklch(0.50 0.16 332)", providers: ["viator", "klook"],
    aliases: ["hong kong", "hkg"], featured: true,
    photo: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&h=400&fit=crop&auto=format&q=80",
  },
  {
    iata: "TPE", name: "Taipei", display: "Taipéi", country: "Taiwán", continent: "asia",
    moods: ["foodie", "arte", "aventura"], vibes: ["night markets", "Taipei 101", "montañas cercanas"],
    gradient: G.asia(196, 188),
    accentColor: "oklch(0.44 0.10 194)", providers: ["viator", "klook"],
    aliases: ["taipéi", "taipei", "taiwan", "taiwán", "tpe"],
  },
  {
    iata: "CNX", name: "Chiang Mai", display: "Chiang Mai", country: "Tailandia", continent: "asia",
    moods: ["bienestar", "aventura", "historia"], vibes: ["templos", "elefantes", "mercado nocturno"],
    gradient: G.asia(44, 36),
    accentColor: "oklch(0.52 0.16 42)", providers: ["viator", "klook"],
    aliases: ["chiang mai", "chiangmai", "cnx"],
  },
  {
    iata: "HKT", name: "Phuket", display: "Phuket", country: "Tailandia", continent: "asia",
    moods: ["bienestar", "naturaleza", "fiesta"], vibes: ["Phi Phi", "Patong", "kayak islas"],
    gradient: G.asia(192, 184),
    accentColor: "oklch(0.44 0.12 190)", providers: ["viator", "klook"],
    aliases: ["phuket", "hkt"],
  },
  {
    iata: "DAD", name: "Da Nang", display: "Da Nang", country: "Vietnam", continent: "asia",
    moods: ["bienestar", "historia", "foodie"], vibes: ["Hội An cercano", "playa My Khê", "Marble Mountains"],
    gradient: G.asia(50, 42),
    accentColor: "oklch(0.52 0.16 48)", providers: ["viator"],
    aliases: ["da nang", "danang", "hoi an", "hội an"],
  },
  {
    iata: "REP", name: "Siem Reap", display: "Siem Reap", country: "Camboya", continent: "asia",
    moods: ["historia", "aventura", "bienestar"], vibes: ["Angkor Wat", "amanecer templo", "vida local"],
    gradient: G.asia(36, 28),
    accentColor: "oklch(0.52 0.16 34)", providers: ["viator"],
    aliases: ["siem reap", "angkor", "cambodia", "camboya"],
  },
  {
    iata: "LPQ", name: "Luang Prabang", display: "Luang Prabang", country: "Laos", continent: "asia",
    moods: ["bienestar", "historia", "naturaleza"], vibes: ["monjes al amanecer", "Mekong", "templos dorados"],
    gradient: G.asia(42, 34),
    accentColor: "oklch(0.52 0.14 40)", providers: ["viator"],
    aliases: ["luang prabang", "laos", "lpq"],
  },
  {
    iata: "KTM", name: "Kathmandu", display: "Katmandú", country: "Nepal", continent: "asia",
    moods: ["aventura", "historia", "bienestar"], vibes: ["Himalaya", "espiritualidad", "Boudhanath"],
    gradient: G.asia(36, 28),
    accentColor: "oklch(0.52 0.16 34)", providers: ["viator"],
    aliases: ["katmandú", "kathmandu", "nepal", "ktm"],
  },
  {
    iata: "CMB", name: "Colombo", display: "Colombo", country: "Sri Lanka", continent: "asia",
    moods: ["foodie", "bienestar", "historia"], vibes: ["té", "playas", "templos"],
    gradient: G.asia(140, 132),
    accentColor: "oklch(0.44 0.10 138)", providers: ["viator"],
    aliases: ["colombo", "sri lanka", "ceilán", "cmb"],
  },
  {
    iata: "MLE", name: "Malé", display: "Maldivas", country: "Maldivas", continent: "asia",
    moods: ["romantico", "bienestar", "naturaleza"], vibes: ["overwater bungalow", "snorkel", "laguna turquesa"],
    gradient: "linear-gradient(145deg, oklch(0.978 0.018 200) 0%, oklch(0.966 0.014 192) 100%)",
    accentColor: "oklch(0.50 0.14 198)", providers: ["viator"],
    aliases: ["maldivas", "maldives", "malé", "male", "mle"],
  },
  {
    iata: "JAI", name: "Jaipur", display: "Jaipur", country: "India", continent: "asia",
    moods: ["historia", "arte", "aventura"], vibes: ["Ciudad Rosa", "Amber Fort", "camellos"],
    gradient: G.asia(42, 34),
    accentColor: "oklch(0.54 0.18 40)", providers: ["viator"],
    aliases: ["jaipur", "ciudad rosa", "jai"],
  },
  {
    iata: "AGR", name: "Agra", display: "Agra", country: "India", continent: "asia",
    moods: ["romantico", "historia", "aventura"], vibes: ["Taj Mahal", "Mughal", "amanecer"],
    gradient: G.asia(40, 32),
    accentColor: "oklch(0.52 0.16 38)", providers: ["viator"],
    aliases: ["agra", "taj mahal", "agr"],
  },
  {
    iata: "VNS", name: "Varanasi", display: "Varanasi", country: "India", continent: "asia",
    moods: ["historia", "bienestar", "aventura"], vibes: ["ghats", "río Ganges", "espiritualidad"],
    gradient: G.asia(36, 28),
    accentColor: "oklch(0.52 0.18 34)", providers: ["viator"],
    aliases: ["varanasi", "benares", "vns"],
  },
  {
    iata: "GOI", name: "Goa", display: "Goa", country: "India", continent: "asia",
    moods: ["bienestar", "naturaleza", "fiesta"], vibes: ["playas", "feni", "iglesias portuguesas"],
    gradient: G.asia(52, 44),
    accentColor: "oklch(0.52 0.14 50)", providers: ["viator"],
    aliases: ["goa", "goi"],
  },
  {
    iata: "LOP", name: "Lombok", display: "Lombok", country: "Indonesia", continent: "asia",
    moods: ["naturaleza", "bienestar", "aventura"], vibes: ["Rinjani", "Gili Islands", "playas vírgenes"],
    gradient: G.asia(144, 136),
    accentColor: "oklch(0.44 0.10 142)", providers: ["viator"],
    aliases: ["lombok", "gili", "lop"],
  },
  {
    iata: "DVO", name: "Davao", display: "Filipinas", country: "Filipinas", continent: "asia",
    moods: ["naturaleza", "aventura", "bienestar"], vibes: ["archipiélago", "snorkel Tubbataha", "Palawan"],
    gradient: G.asia(188, 180),
    accentColor: "oklch(0.44 0.12 186)", providers: ["viator", "klook"],
    aliases: ["filipinas", "philippines", "palawan", "cebu", "manila", "dvo"],
  },
  {
    iata: "NRT", name: "Nara", display: "Nara", country: "Japón", continent: "asia",
    moods: ["historia", "naturaleza", "bienestar"], vibes: ["ciervos libres", "Tōdai-ji", "antigua capital"],
    gradient: G.asia(136, 128),
    accentColor: "oklch(0.44 0.10 134)", providers: ["viator"],
    aliases: ["nara", "nrt"],
  },
  {
    iata: "HIJ", name: "Hiroshima", display: "Hiroshima", country: "Japón", continent: "asia",
    moods: ["historia", "bienestar", "naturaleza"], vibes: ["paz", "isla Miyajima", "okonomiyaki"],
    gradient: G.asia(332, 324),
    accentColor: "oklch(0.50 0.14 330)", providers: ["viator"],
    aliases: ["hiroshima", "hij", "miyajima"],
  },
  {
    iata: "OKA", name: "Okinawa", display: "Okinawa", country: "Japón", continent: "asia",
    moods: ["bienestar", "naturaleza", "historia"], vibes: ["longevidad", "playas tropicales", "castillos Ryukyu"],
    gradient: G.asia(196, 188),
    accentColor: "oklch(0.44 0.10 194)", providers: ["viator"],
    aliases: ["okinawa", "oka"],
  },

  // ══════════════════════════════════════════════════════════════════════════
  // AMÉRICA LATINA — nuevas ciudades
  // ══════════════════════════════════════════════════════════════════════════

  {
    iata: "OAX", name: "Oaxaca", display: "Oaxaca", country: "México", continent: "america_latina",
    moods: ["foodie", "historia", "arte"], vibes: ["mole", "Monte Albán", "artesanías"],
    gradient: G.america_latina(44, 36),
    accentColor: "oklch(0.54 0.18 42)", providers: ["viator"],
    aliases: ["oaxaca", "oax"],
  },
  {
    iata: "MID", name: "Merida", display: "Mérida", country: "México", continent: "america_latina",
    moods: ["historia", "foodie", "aventura"], vibes: ["maya", "cenotes", "haciendas"],
    gradient: G.america_latina(52, 44),
    accentColor: "oklch(0.52 0.16 50)", providers: ["viator"],
    aliases: ["mérida", "merida", "mid", "yucatán"],
  },
  {
    iata: "TNM", name: "Tulum", display: "Tulum", country: "México", continent: "america_latina",
    moods: ["bienestar", "naturaleza", "aventura"], vibes: ["cenotes", "ruinas mayas", "eco-chic"],
    gradient: G.america_latina(192, 184),
    accentColor: "oklch(0.46 0.12 190)", providers: ["viator"],
    aliases: ["tulum", "tnm"],
  },
  {
    iata: "BJX", name: "Guanajuato", display: "Guanajuato", country: "México", continent: "america_latina",
    moods: ["historia", "arte", "romantico"], vibes: ["callejones", "mumias", "colonial"],
    gradient: G.america_latina(42, 34),
    accentColor: "oklch(0.54 0.18 40)", providers: ["viator"],
    aliases: ["guanajuato", "bjx"],
  },
  {
    iata: "MTY", name: "Monterrey", display: "Monterrey", country: "México", continent: "america_latina",
    moods: ["aventura", "foodie"], vibes: ["cañón", "cabrito", "industrial moderna"],
    gradient: G.america_latina(36, 28),
    accentColor: "oklch(0.50 0.14 34)", providers: ["viator"],
    aliases: ["monterrey", "mty"],
  },
  {
    iata: "AUA", name: "Antigua", display: "Antigua", country: "Guatemala", continent: "america_latina",
    moods: ["historia", "aventura", "foodie"], vibes: ["colonial", "volcán Acatenango", "café"],
    gradient: G.america_latina(40, 32),
    accentColor: "oklch(0.52 0.16 38)", providers: ["viator"],
    aliases: ["antigua", "guatemala", "aua"],
  },
  {
    iata: "SMR", name: "Santa Marta", display: "Santa Marta", country: "Colombia", continent: "america_latina",
    moods: ["naturaleza", "bienestar", "aventura"], vibes: ["Ciudad Perdida", "Parque Tayrona", "playa"],
    gradient: G.america_latina(188, 180),
    accentColor: "oklch(0.44 0.12 186)", providers: ["viator"],
    aliases: ["santa marta", "smr", "tayrona"],
  },
  {
    iata: "CLO", name: "Cali", display: "Cali", country: "Colombia", continent: "america_latina",
    moods: ["fiesta", "bienestar", "foodie"], vibes: ["salsa", "termales", "chontaduro"],
    gradient: G.america_latina(44, 36),
    accentColor: "oklch(0.52 0.18 42)", providers: ["viator"],
    aliases: ["cali", "clo"],
  },
  {
    iata: "IGR", name: "Iguazu", display: "Iguazú", country: "Argentina", continent: "america_latina",
    moods: ["naturaleza", "aventura", "bienestar"], vibes: ["cataratas", "selva misionera", "tucanes"],
    gradient: G.america_latina(136, 128),
    accentColor: "oklch(0.44 0.10 134)", providers: ["viator"],
    aliases: ["iguazú", "iguazu", "cataratas", "igr"],
  },
  {
    iata: "BHC", name: "Bariloche", display: "Bariloche", country: "Argentina", continent: "america_latina",
    moods: ["naturaleza", "aventura", "bienestar"], vibes: ["Patagonia", "lagos", "chocolate suizo"],
    gradient: G.america_latina(208, 200),
    accentColor: "oklch(0.44 0.10 206)", providers: ["viator"],
    aliases: ["bariloche", "patagonia argentina", "bhc"],
  },
  {
    iata: "SLA", name: "Salta", display: "Salta", country: "Argentina", continent: "america_latina",
    moods: ["historia", "naturaleza", "aventura"], vibes: ["Puna", "quebrada Humahuaca", "Tren a las Nubes"],
    gradient: G.america_latina(44, 36),
    accentColor: "oklch(0.52 0.16 42)", providers: ["viator"],
    aliases: ["salta", "sla"],
  },
  {
    iata: "UYU", name: "Uyuni", display: "Salar de Uyuni", country: "Bolivia", continent: "america_latina",
    moods: ["naturaleza", "aventura", "bienestar"], vibes: ["salar infinito", "cielo de estrellas", "Altiplano"],
    gradient: "linear-gradient(145deg, oklch(0.982 0.016 216) 0%, oklch(0.970 0.012 208) 100%)",
    accentColor: "oklch(0.46 0.10 214)", providers: ["viator"],
    aliases: ["salar de uyuni", "uyuni", "bolivia", "uyu"],
  },
  {
    iata: "LPB", name: "La Paz", display: "La Paz", country: "Bolivia", continent: "america_latina",
    moods: ["historia", "aventura", "foodie"], vibes: ["ciudad más alta", "Witches Market", "cable car"],
    gradient: G.america_latina(216, 208),
    accentColor: "oklch(0.44 0.10 214)", providers: ["viator"],
    aliases: ["la paz", "lpb"],
  },
  {
    iata: "TBP", name: "Mancora", display: "Máncora", country: "Perú", continent: "america_latina",
    moods: ["bienestar", "naturaleza", "fiesta"], vibes: ["surf", "playa tropical", "ceviche"],
    gradient: G.america_latina(192, 184),
    accentColor: "oklch(0.46 0.12 190)", providers: ["viator"],
    aliases: ["máncora", "mancora", "tbp"],
  },
  {
    iata: "FLN", name: "Florianopolis", display: "Florianópolis", country: "Brasil", continent: "america_latina",
    moods: ["bienestar", "naturaleza", "fiesta"], vibes: ["playas", "Lagoa da Conceição", "surf"],
    gradient: G.america_latina(194, 186),
    accentColor: "oklch(0.46 0.12 192)", providers: ["viator"],
    aliases: ["florianópolis", "florianopolis", "floripa", "fln"],
  },
  {
    iata: "REC", name: "Recife", display: "Recife", country: "Brasil", continent: "america_latina",
    moods: ["fiesta", "bienestar", "historia"], vibes: ["frevo", "arrecife", "carnaval"],
    gradient: G.america_latina(42, 34),
    accentColor: "oklch(0.52 0.18 40)", providers: ["viator"],
    aliases: ["recife", "olinda"],
  },
  {
    iata: "FOR", name: "Fortaleza", display: "Fortaleza", country: "Brasil", continent: "america_latina",
    moods: ["bienestar", "naturaleza", "aventura"], vibes: ["dunas", "jangadas", "langosta"],
    gradient: G.america_latina(50, 42),
    accentColor: "oklch(0.52 0.16 48)", providers: ["viator"],
    aliases: ["fortaleza"],
  },
  {
    iata: "PDP", name: "Punta del Este", display: "Punta del Este", country: "Uruguay", continent: "america_latina",
    moods: ["bienestar", "fiesta", "arte"], vibes: ["Casapueblo", "playa Brava", "gastronomía"],
    gradient: G.america_latina(204, 196),
    accentColor: "oklch(0.46 0.10 202)", providers: ["viator"],
    aliases: ["punta del este", "pdp", "pde"],
  },
  {
    iata: "VAP", name: "Valparaiso", display: "Valparaíso", country: "Chile", continent: "america_latina",
    moods: ["arte", "aventura", "romantico"], vibes: ["murales", "ascensores", "cerros de colores"],
    gradient: G.america_latina(220, 212),
    accentColor: "oklch(0.46 0.12 218)", providers: ["viator"],
    aliases: ["valparaíso", "valparaiso", "viña del mar", "vap"],
  },
  {
    iata: "CJC", name: "Calama", display: "Desierto de Atacama", country: "Chile", continent: "america_latina",
    moods: ["naturaleza", "aventura", "bienestar"], vibes: ["desierto más árido", "Valle de la Luna", "estrellas"],
    gradient: G.america_latina(44, 36),
    accentColor: "oklch(0.52 0.16 42)", providers: ["viator"],
    aliases: ["atacama", "san pedro de atacama", "cjc", "calama"],
  },
  {
    iata: "PUQ", name: "Punta Arenas", display: "Patagonia Chilena", country: "Chile", continent: "america_latina",
    moods: ["naturaleza", "aventura"], vibes: ["Torres del Paine", "glaciares", "fin del mundo"],
    gradient: G.america_latina(208, 200),
    accentColor: "oklch(0.44 0.10 206)", providers: ["viator"],
    aliases: ["patagonia", "torres del paine", "punta arenas", "puq"],
  },
  {
    iata: "ASU", name: "Asuncion", display: "Asunción", country: "Paraguay", continent: "america_latina",
    moods: ["historia", "foodie"], vibes: ["palacio de gobierno", "mbejú", "tranquila"],
    gradient: G.america_latina(38, 30),
    accentColor: "oklch(0.50 0.14 36)", providers: ["viator"],
    aliases: ["asunción", "asuncion", "paraguay", "asu"],
  },
  {
    iata: "TND", name: "Trinidad", display: "Trinidad", country: "Cuba", continent: "america_latina",
    moods: ["historia", "romantico", "bienestar"], vibes: ["colonial azucarero", "empedrado", "Valle de los Ingenios"],
    gradient: G.america_latina(50, 42),
    accentColor: "oklch(0.52 0.16 48)", providers: ["viator"],
    aliases: ["trinidad cuba", "tnd"],
  },
  {
    iata: "POS", name: "Port of Spain", display: "Trinidad y Tobago", country: "Trinidad y Tobago", continent: "america_latina",
    moods: ["fiesta", "bienestar", "naturaleza"], vibes: ["carnaval", "calipso", "arrecife Tobago"],
    gradient: G.america_latina(136, 128),
    accentColor: "oklch(0.44 0.12 134)", providers: ["viator"],
    aliases: ["trinidad y tobago", "tobago", "pos"],
  },

  // ══════════════════════════════════════════════════════════════════════════
  // AMÉRICA DEL NORTE — nuevas ciudades
  // ══════════════════════════════════════════════════════════════════════════

  {
    iata: "MSY", name: "New Orleans", display: "Nueva Orleans", country: "Estados Unidos", continent: "america_norte",
    moods: ["fiesta", "foodie", "historia"], vibes: ["jazz", "beignets", "Mardi Gras"],
    gradient: G.america_norte(44, 36),
    accentColor: "oklch(0.52 0.16 42)", providers: ["viator"],
    aliases: ["nueva orleans", "new orleans", "nola", "msy"],
  },
  {
    iata: "DCA", name: "Washington DC", display: "Washington DC", country: "Estados Unidos", continent: "america_norte",
    moods: ["historia", "arte"], vibes: ["monumentos", "Smithsonian gratis", "política"],
    gradient: G.america_norte(212, 204),
    accentColor: "oklch(0.44 0.10 210)", providers: ["viator"],
    aliases: ["washington dc", "washington d.c.", "dc", "dca"],
  },
  {
    iata: "BOS", name: "Boston", display: "Boston", country: "Estados Unidos", continent: "america_norte",
    moods: ["historia", "foodie", "arte"], vibes: ["Harvard", "langosta", "Freedom Trail"],
    gradient: G.america_norte(208, 200),
    accentColor: "oklch(0.44 0.10 206)", providers: ["viator"],
    aliases: ["boston", "bos"],
  },
  {
    iata: "SEA", name: "Seattle", display: "Seattle", country: "Estados Unidos", continent: "america_norte",
    moods: ["foodie", "naturaleza", "arte"], vibes: ["café", "Pike Place", "volcán Rainier"],
    gradient: G.america_norte(200, 192),
    accentColor: "oklch(0.44 0.10 198)", providers: ["viator"],
    aliases: ["seattle"],
  },
  {
    iata: "LAS", name: "Las Vegas", display: "Las Vegas", country: "Estados Unidos", continent: "america_norte",
    moods: ["fiesta", "aventura"], vibes: ["Strip", "Grand Canyon cercano", "shows"],
    gradient: G.america_norte(44, 36),
    accentColor: "oklch(0.52 0.16 42)", providers: ["viator"],
    aliases: ["las vegas", "vegas"],
  },
  {
    iata: "AUS", name: "Austin", display: "Austin", country: "Estados Unidos", continent: "america_norte",
    moods: ["fiesta", "foodie", "arte"], vibes: ["live music", "BBQ", "KeepAustinWeird"],
    gradient: G.america_norte(40, 32),
    accentColor: "oklch(0.52 0.16 38)", providers: ["viator"],
    aliases: ["austin", "aus"],
  },
  {
    iata: "DEN", name: "Denver", display: "Denver", country: "Estados Unidos", continent: "america_norte",
    moods: ["aventura", "naturaleza", "foodie"], vibes: ["Montañas Rocosas", "cerveza artesanal", "nieve"],
    gradient: G.america_norte(204, 196),
    accentColor: "oklch(0.44 0.10 202)", providers: ["viator"],
    aliases: ["denver"],
  },
  {
    iata: "BNA", name: "Nashville", display: "Nashville", country: "Estados Unidos", continent: "america_norte",
    moods: ["fiesta", "foodie"], vibes: ["country music", "hot chicken", "Broadway"],
    gradient: G.america_norte(42, 34),
    accentColor: "oklch(0.52 0.14 40)", providers: ["viator"],
    aliases: ["nashville", "bna"],
  },
  {
    iata: "YUL", name: "Montreal", display: "Montreal", country: "Canadá", continent: "america_norte",
    moods: ["foodie", "arte", "fiesta"], vibes: ["festival jazz", "bagel Montreal", "bilingüe"],
    gradient: G.america_norte(214, 206),
    accentColor: "oklch(0.44 0.10 212)", providers: ["viator"],
    aliases: ["montreal", "yul"],
  },
  {
    iata: "YVR", name: "Vancouver", display: "Vancouver", country: "Canadá", continent: "america_norte",
    moods: ["naturaleza", "foodie", "bienestar"], vibes: ["montaña y mar", "Whistler", "multicultural"],
    gradient: G.america_norte(196, 188),
    accentColor: "oklch(0.44 0.10 194)", providers: ["viator"],
    aliases: ["vancouver", "yvr"],
  },
  {
    iata: "YQB", name: "Quebec City", display: "Quebec City", country: "Canadá", continent: "america_norte",
    moods: ["romantico", "historia", "foodie"], vibes: ["Château Frontenac", "invierno", "cocina francesa"],
    gradient: G.america_norte(48, 40),
    accentColor: "oklch(0.50 0.14 46)", providers: ["viator"],
    // Supply Coverage V2.1: bare "quebec" (no accent) and the "ciudad de
    // quebec/québec" phrasings were missing — only the exact auto-typed
    // "quebec city"/"québec" ever matched, so a plain "Quebec 3 días..."
    // query silently fell through to "coming soon".
    aliases: [
      "quebec city", "québec", "quebec", "yqb",
      "ciudad de quebec", "ciudad de québec",
    ],
  },

  // ══════════════════════════════════════════════════════════════════════════
  // MEDIO ORIENTE — nuevas ciudades
  // ══════════════════════════════════════════════════════════════════════════

  {
    iata: "AUH", name: "Abu Dhabi", display: "Abu Dhabi", country: "EAU", continent: "medio_oriente",
    moods: ["arte", "bienestar", "historia"], vibes: ["mezquita Sheikh Zayed", "Louvre Abu Dhabi", "desierto"],
    gradient: G.medio_oriente(50, 42),
    accentColor: "oklch(0.52 0.14 48)", providers: ["viator"],
    aliases: ["abu dhabi", "abu dabi", "auh"],
  },
  {
    iata: "MCT", name: "Muscat", display: "Mascate", country: "Omán", continent: "medio_oriente",
    moods: ["historia", "naturaleza", "bienestar"], vibes: ["wadis", "Al Alam Palace", "souq Muttrah"],
    gradient: G.medio_oriente(44, 36),
    accentColor: "oklch(0.52 0.14 42)", providers: ["viator"],
    aliases: ["mascate", "muscat", "omán", "oman", "mct"],
  },
  {
    iata: "TLV", name: "Tel Aviv", display: "Tel Aviv", country: "Israel", continent: "medio_oriente",
    moods: ["foodie", "fiesta", "arte"], vibes: ["Bauhaus", "hummus", "playa mediterránea"],
    gradient: G.medio_oriente(52, 44),
    accentColor: "oklch(0.52 0.16 50)", providers: ["viator"],
    aliases: ["tel aviv", "israel", "tlv"],
  },
  {
    iata: "JRS", name: "Jerusalem", display: "Jerusalén", country: "Israel", continent: "medio_oriente",
    moods: ["historia", "bienestar", "aventura"], vibes: ["Ciudad Antigua", "Muro Occidental", "espiritualidad"],
    gradient: G.medio_oriente(46, 38),
    accentColor: "oklch(0.52 0.16 44)", providers: ["viator"],
    aliases: ["jerusalén", "jerusalem", "jrs"],
  },
  {
    iata: "BEY", name: "Beirut", display: "Beirut", country: "Líbano", continent: "medio_oriente",
    moods: ["foodie", "historia", "fiesta"], vibes: ["mézze", "Gemmayze", "phoenicia"],
    gradient: G.medio_oriente(36, 28),
    accentColor: "oklch(0.52 0.18 34)", providers: ["viator"],
    aliases: ["beirut", "líbano", "libano", "bey"],
  },
  {
    iata: "JED", name: "Jeddah", display: "Jeddah", country: "Arabia Saudita", continent: "medio_oriente",
    moods: ["historia", "foodie", "bienestar"], vibes: ["Al-Balad", "mar Rojo", "corniche"],
    gradient: G.medio_oriente(42, 34),
    accentColor: "oklch(0.52 0.14 40)", providers: ["viator"],
    aliases: ["jeddah", "jidda", "jed"],
  },
  {
    iata: "RUH", name: "Riyadh", display: "Riad", country: "Arabia Saudita", continent: "medio_oriente",
    moods: ["historia", "aventura"], vibes: ["Diriyah", "Kingdom Tower", "visión 2030"],
    gradient: G.medio_oriente(38, 30),
    accentColor: "oklch(0.52 0.14 36)", providers: ["viator"],
    aliases: ["riad", "riyadh", "ruh"],
  },
  {
    iata: "KWI", name: "Kuwait City", display: "Kuwait City", country: "Kuwait", continent: "medio_oriente",
    moods: ["historia", "foodie"], vibes: ["Torres de agua", "souq Mubarakiya", "descanso Golfo"],
    gradient: G.medio_oriente(46, 38),
    accentColor: "oklch(0.52 0.14 44)", providers: ["viator"],
    aliases: ["kuwait", "kwi"],
  },

  // ══════════════════════════════════════════════════════════════════════════
  // ÁFRICA — nuevas ciudades
  // ══════════════════════════════════════════════════════════════════════════

  {
    iata: "NBO", name: "Nairobi", display: "Nairobi", country: "Kenia", continent: "africa",
    moods: ["aventura", "naturaleza", "historia"], vibes: ["safari base", "Masái Mara", "rhinos"],
    gradient: G.africa(136, 128),
    accentColor: "oklch(0.44 0.10 134)", providers: ["viator"],
    aliases: ["nairobi", "kenia", "kenya", "nbo"],
  },
  {
    iata: "ZNZ", name: "Zanzibar", display: "Zanzíbar", country: "Tanzania", continent: "africa",
    moods: ["bienestar", "romantico", "historia"], vibes: ["Stone Town", "especias", "playas blancas"],
    gradient: "linear-gradient(145deg, oklch(0.984 0.022 56) 0%, oklch(0.972 0.018 48) 100%)",
    accentColor: "oklch(0.54 0.16 54)", providers: ["viator"],
    aliases: ["zanzíbar", "zanzibar", "znz"],
  },
  {
    iata: "JRO", name: "Kilimanjaro", display: "Kilimanjaro", country: "Tanzania", continent: "africa",
    moods: ["aventura", "naturaleza"], vibes: ["cima más alta África", "trekking", "Serengeti base"],
    gradient: G.africa(132, 124),
    accentColor: "oklch(0.44 0.10 130)", providers: ["viator"],
    aliases: ["kilimanjaro", "serengeti", "tanzania", "jro"],
  },
  {
    iata: "MBA", name: "Mombasa", display: "Mombasa", country: "Kenia", continent: "africa",
    moods: ["bienestar", "naturaleza", "historia"], vibes: ["costa swahili", "playas", "Fort Jesus"],
    gradient: G.africa(44, 36),
    accentColor: "oklch(0.52 0.16 42)", providers: ["viator"],
    aliases: ["mombasa", "mba"],
  },
  {
    iata: "FEZ", name: "Fez", display: "Fez", country: "Marruecos", continent: "africa",
    moods: ["historia", "arte", "foodie"], vibes: ["medina medieval", "tintes de cuero", "artesanía árabe"],
    gradient: G.africa(42, 34),
    accentColor: "oklch(0.52 0.16 40)", providers: ["viator"],
    aliases: ["fez", "fès", "fes", "fez"],
  },
  {
    iata: "CMN", name: "Casablanca", display: "Casablanca", country: "Marruecos", continent: "africa",
    moods: ["historia", "foodie", "arte"], vibes: ["Hassan II", "Art Deco", "corniche"],
    gradient: G.africa(222, 214),
    accentColor: "oklch(0.44 0.10 220)", providers: ["viator"],
    aliases: ["casablanca", "cmn", "casa"],
  },
  {
    iata: "DSS", name: "Dakar", display: "Dakar", country: "Senegal", continent: "africa",
    moods: ["historia", "foodie", "arte"], vibes: ["Île de Gorée", "thiéboudienne", "música mbalax"],
    gradient: G.africa(44, 36),
    accentColor: "oklch(0.52 0.18 42)", providers: ["viator"],
    aliases: ["dakar", "senegal", "dss"],
  },
  {
    iata: "ACC", name: "Accra", display: "Acra", country: "Ghana", continent: "africa",
    moods: ["historia", "foodie", "aventura"], vibes: ["Year of Return", "fufu", "Labadi Beach"],
    gradient: G.africa(40, 32),
    accentColor: "oklch(0.52 0.16 38)", providers: ["viator"],
    aliases: ["acra", "accra", "ghana", "acc"],
  },
  {
    iata: "ADD", name: "Addis Ababa", display: "Adís Abeba", country: "Etiopía", continent: "africa",
    moods: ["historia", "foodie", "aventura"], vibes: ["injera", "Lalibela base", "origen café"],
    gradient: G.africa(36, 28),
    accentColor: "oklch(0.52 0.16 34)", providers: ["viator"],
    aliases: ["adís abeba", "addis abeba", "addis ababa", "etiopía"],
  },
  {
    iata: "TUN", name: "Tunis", display: "Túnez", country: "Túnez", continent: "africa",
    moods: ["historia", "foodie", "bienestar"], vibes: ["medina patrimonio", "Cartago", "playas mediterráneas"],
    gradient: G.africa(48, 40),
    accentColor: "oklch(0.52 0.14 46)", providers: ["viator"],
    aliases: ["túnez", "tunis", "tun"],
  },
  {
    iata: "SEZ", name: "Mahe", display: "Seychelles", country: "Seychelles", continent: "africa",
    moods: ["romantico", "bienestar", "naturaleza"], vibes: ["granito tropical", "coco de mer", "playas Anse Lazio"],
    gradient: "linear-gradient(145deg, oklch(0.984 0.022 186) 0%, oklch(0.972 0.018 178) 100%)",
    accentColor: "oklch(0.46 0.12 184)", providers: ["viator"],
    aliases: ["seychelles", "mahé", "mahe", "sez"],
  },
  {
    iata: "MRU", name: "Mauritius", display: "Mauricio", country: "Mauricio", continent: "africa",
    moods: ["bienestar", "romantico", "naturaleza"], vibes: ["laguna turquesa", "Le Morne", "rum"],
    gradient: "linear-gradient(145deg, oklch(0.982 0.018 192) 0%, oklch(0.970 0.014 184) 100%)",
    accentColor: "oklch(0.46 0.12 190)", providers: ["viator"],
    aliases: ["mauricio", "mauritius", "mru"],
  },

  // ══════════════════════════════════════════════════════════════════════════
  // OCEANÍA — nuevas ciudades
  // ══════════════════════════════════════════════════════════════════════════

  {
    iata: "MEL", name: "Melbourne", display: "Melbourne", country: "Australia", continent: "oceania",
    moods: ["foodie", "arte", "bienestar"], vibes: ["coffee culture", "grafiti Hosier Lane", "Great Ocean Road"],
    gradient: G.oceania(200, 192),
    accentColor: "oklch(0.44 0.10 198)", providers: ["viator"],
    aliases: ["melbourne", "mel"], featured: true,
    photo: "https://images.unsplash.com/photo-1545044846-351ba102b6d5?w=600&h=400&fit=crop&auto=format&q=80",
  },
  {
    iata: "BNE", name: "Brisbane", display: "Brisbane", country: "Australia", continent: "oceania",
    moods: ["bienestar", "aventura", "naturaleza"], vibes: ["Gold Coast base", "koalas", "sol"],
    gradient: G.oceania(44, 36),
    accentColor: "oklch(0.52 0.14 42)", providers: ["viator"],
    aliases: ["brisbane", "bne"],
  },
  {
    iata: "CNS", name: "Cairns", display: "Cairns", country: "Australia", continent: "oceania",
    moods: ["naturaleza", "aventura", "bienestar"], vibes: ["Gran Barrera Coral", "selva Daintree", "buceo"],
    gradient: G.oceania(192, 184),
    accentColor: "oklch(0.44 0.12 190)", providers: ["viator"],
    aliases: ["cairns", "cns", "great barrier reef"],
  },
  {
    iata: "AKL", name: "Auckland", display: "Auckland", country: "Nueva Zelanda", continent: "oceania",
    moods: ["naturaleza", "aventura", "foodie"], vibes: ["Waiheke Island", "Sky Tower", "maoríes"],
    gradient: G.oceania(196, 188),
    accentColor: "oklch(0.44 0.10 194)", providers: ["viator"],
    aliases: ["auckland", "nueva zelanda", "new zealand", "akl"],
  },
  {
    iata: "ZQN", name: "Queenstown", display: "Queenstown", country: "Nueva Zelanda", continent: "oceania",
    moods: ["aventura", "naturaleza", "bienestar"], vibes: ["bungee jumping", "Fiordland", "ski"],
    gradient: G.oceania(204, 196),
    accentColor: "oklch(0.44 0.10 202)", providers: ["viator"],
    aliases: ["queenstown", "zqn", "fiordland", "milford sound"],
  },
  {
    iata: "PPT", name: "Papeete", display: "Bora Bora", country: "Polinesia Francesa", continent: "oceania",
    moods: ["romantico", "bienestar", "naturaleza"], vibes: ["bungalow sobre el agua", "laguna azul", "Tahití"],
    gradient: "linear-gradient(145deg, oklch(0.980 0.018 198) 0%, oklch(0.968 0.014 190) 100%)",
    accentColor: "oklch(0.46 0.14 196)", providers: ["viator"],
    aliases: ["bora bora", "tahití", "tahiti", "polinesia francesa", "ppt"],
  },
  {
    iata: "NAN", name: "Nadi", display: "Fiyi", country: "Fiyi", continent: "oceania",
    moods: ["bienestar", "romantico", "naturaleza"], vibes: ["Mamanuca Islands", "buceo", "kava"],
    gradient: G.oceania(44, 36),
    accentColor: "oklch(0.52 0.14 42)", providers: ["viator"],
    aliases: ["fiyi", "fiji", "nadi"],
  },

] as const;

// ─── Índices derivados ────────────────────────────────────────────────────────
// Se generan UNA VEZ al cargar el módulo para búsquedas O(1).

/** IATA → CatalogCity */
export const CATALOG_BY_IATA: Readonly<Record<string, CatalogCity>> =
  Object.fromEntries(CITY_CATALOG.map(c => [c.iata, c]));

/** alias (lowercase) → IATA */
export const CATALOG_ALIAS_MAP: Readonly<Record<string, string>> =
  Object.fromEntries(
    CITY_CATALOG.flatMap(c => c.aliases.map(a => [a, c.iata]))
  );

/** Ciudades destacadas para la vista "sugeridos" (sin filtro de mood) */
export const FEATURED_CITIES: readonly CatalogCity[] =
  CITY_CATALOG.filter(c => c.featured);

/** Ciudades agrupadas por continente */
export const CITIES_BY_CONTINENT: Readonly<Record<Continent, CatalogCity[]>> =
  CITY_CATALOG.reduce(
    (acc, c) => {
      (acc[c.continent] ??= []).push(c);
      return acc;
    },
    {} as Record<Continent, CatalogCity[]>,
  );

/** Ciudades agrupadas por mood */
export const CITIES_BY_MOOD: Readonly<Record<MoodId, CatalogCity[]>> =
  CITY_CATALOG.reduce(
    (acc, c) => {
      for (const mood of c.moods) (acc[mood] ??= []).push(c);
      return acc;
    },
    {} as Record<MoodId, CatalogCity[]>,
  );
